import React from "react";
import { createPortal } from "react-dom";
import {
  Loader2,
  X,
  CheckCheck,
  Trash2,
  CheckSquare,
  AlertTriangle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useToast } from "../feedback/ToastProvider";
import NotificationItem from "./NotificationItem";

export default function NotificationModal({
  notifications = [],
  loading,
  refreshing = false,
  onMarkRead,
  onMarkAllRead,
  onMarkSelectedRead,
  onDeleteNotifications,
  onClose,
}) {
  const { t } = useTranslation("notifications");

  const [isClosing, setIsClosing] = React.useState(false);
  const [tab, setTab] = React.useState("all");
  const [selectionMode, setSelectionMode] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [confirmDialog, setConfirmDialog] = React.useState({
    open: false,
    title: "",
    text: "",
    action: null,
  });
  const { showToast } = useToast();
  const unread = notifications.filter((n) => !n.is_read);

  const visible = React.useMemo(() => {
    if (tab === "unread") return notifications.filter((n) => !n.is_read);
    if (tab === "read") return notifications.filter((n) => n.is_read);
    return notifications;
  }, [notifications, tab]);

  const unreadNotifications = visible.filter((n) => !n.is_read);
  const readNotifications = visible.filter((n) => n.is_read);
  const [isDeleting, setIsDeleting] = React.useState(false);

  function closeWithAnimation() {
    setIsClosing(true);

    window.setTimeout(() => {
      onClose?.();
      setIsClosing(false);
    }, 180);
  }

  function handleToggle(id) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function handleSelectAll() {
    setSelectedIds(visible.map((n) => n.id));
  }

  function handleClearSelection() {
    setSelectedIds([]);
  }

  function toggleSelectionMode() {
    setSelectionMode((prev) => !prev);
    setSelectedIds([]);
  }

  async function handleMarkAllRead() {
    try {
      await onMarkAllRead?.();
      showToast({
        type: "success",
        title: t("messages.markedRead"),
      });
    } catch {
      showToast({
        type: "error",
        title: t("messages.updateFailed"),
      });
    }
  }

  function requestDeleteSelected() {
    if (selectedIds.length === 0) return;

    setConfirmDialog({
      open: true,
      title: t("confirmations.deleteTitle"),
      text: t("confirmations.deleteMessage", {
        count: selectedIds.length,
      }),
      action: async () => {
        try {
          setIsDeleting(true);

          await onDeleteNotifications?.(selectedIds);

          showToast({
            type: "success",
            title: t("messages.deletedCount", {
              count: selectedIds.length,
            }),
          });

          setSelectedIds([]);
          setSelectionMode(false);

          setConfirmDialog({
            open: false,
            title: "",
            text: "",
            action: null,
          });
        } catch {
          showToast({
            type: "error",
            title: t("messages.deleteFailed"),
          });
        } finally {
          setIsDeleting(false);
        }
      },
    });
  }

  const totalCount = notifications.length;
  const unreadCount = unread.length;
  const readCount = notifications.length - unread.length;

  React.useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        if (confirmDialog.open) {
          setConfirmDialog({
            open: false,
            title: "",
            text: "",
            action: null,
          });
          return;
        }

        closeWithAnimation();
      }
    }

    const previousOverflow = document.body.style.overflow;
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [confirmDialog.open]);

  const selectedUnreadIds = React.useMemo(
    () =>
      selectedIds.filter((id) => {
        const notification = notifications.find(
          (item) => Number(item.id) === Number(id),
        );

        return notification && !notification.is_read;
      }),
    [notifications, selectedIds],
  );

  const [isMarkingSelectedRead, setIsMarkingSelectedRead] =
    React.useState(false);

  async function handleMarkSelectedRead() {
    if (selectedUnreadIds.length === 0) {
      return;
    }

    try {
      setIsMarkingSelectedRead(true);

      await onMarkSelectedRead?.(selectedUnreadIds);

      showToast({
        type: "success",
        title: t(
          "messages.selectedMarkedRead",
          "{{count}} notifications marked as read.",
          {
            count: selectedUnreadIds.length,
          },
        ),
      });

      setSelectedIds([]);
      setSelectionMode(false);
    } catch (error) {
      showToast({
        type: "error",
        title: t("messages.updateFailed", "Could not update notifications."),
        message: error?.message || "",
      });
    } finally {
      setIsMarkingSelectedRead(false);
    }
  }

  return createPortal(
    <div
      className={`notification-modal-backdrop ${isClosing ? "is-closing" : ""}`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          closeWithAnimation();
        }
      }}
    >
      <div
        className="notification-modal"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="notification-modal__header">
          <div>
            <h2>{t("modal.title")}</h2>
            <p>
              {t("modal.unread", { count: unread.length })}
              {refreshing ? (
                <span className="notification-modal__refreshing">
                  <Loader2 size={12} className="spin" />
                  {t("messages.refreshing")}
                </span>
              ) : null}
            </p>
          </div>

          <button
            type="button"
            className="notification-modal__close"
            onClick={closeWithAnimation}
            aria-label={t("buttons.close")}
          >
            <X size={22} />
          </button>
        </div>

        <div className="notification-modal__tabs">
          <button
            type="button"
            className={tab === "all" ? "active" : ""}
            onClick={() => {
              setTab("all");
              setSelectedIds([]);
            }}
          >
            <span>{t("tabs.all")}</span>
            <span className="notification-modal__tab-count">{totalCount}</span>
          </button>

          <button
            type="button"
            className={tab === "unread" ? "active" : ""}
            onClick={() => {
              setTab("unread");
              setSelectedIds([]);
            }}
          >
            <span>{t("tabs.unread")}</span>
            <span className="notification-modal__tab-count">{unreadCount}</span>
          </button>

          <button
            type="button"
            className={tab === "read" ? "active" : ""}
            onClick={() => {
              setTab("read");
              setSelectedIds([]);
            }}
          >
            <span>{t("tabs.read")}</span>
            <span className="notification-modal__tab-count">{readCount}</span>
          </button>
        </div>

        <div className="notification-modal__toolbar">
          <button type="button" onClick={toggleSelectionMode}>
            <CheckSquare size={16} />
            {selectionMode ? t("buttons.cancel") : t("buttons.select")}
          </button>

          {selectionMode ? (
            <>
              <button type="button" onClick={handleSelectAll}>
                {t("buttons.selectAll")}
              </button>

              <button type="button" onClick={handleClearSelection}>
                {t("buttons.clearSelection")}
              </button>

              {selectedUnreadIds.length > 0 ? (
                <button
                  type="button"
                  onClick={handleMarkSelectedRead}
                  disabled={isMarkingSelectedRead}
                >
                  {isMarkingSelectedRead ? (
                    <Loader2 size={16} className="spin" />
                  ) : (
                    <CheckCheck size={16} />
                  )}

                  {t("buttons.markSelectedRead", "Mark as read ({{count}})", {
                    count: selectedUnreadIds.length,
                  })}
                </button>
              ) : null}

              <button
                type="button"
                className="danger"
                onClick={requestDeleteSelected}
                disabled={selectedIds.length === 0}
              >
                <Trash2 size={16} />
                {t("buttons.deleteSelectedWithCount", {
                  count: selectedIds.length,
                })}
              </button>
            </>
          ) : (
            tab !== "read" &&
            unread.length > 0 && (
              <button type="button" onClick={handleMarkAllRead}>
                <CheckCheck size={16} />
                {t("buttons.markAllRead")}
              </button>
            )
          )}
        </div>

        <div className="notification-modal__body">
          {loading ? (
            <div className="notification-empty">
              <Loader2 className="spin" />
              {t("messages.loading")}
            </div>
          ) : visible.length === 0 ? (
            <div className="notification-empty">
              {tab === "unread"
                ? t("messages.noUnread", "You have no unread notifications.")
                : tab === "read"
                  ? t("messages.noRead", "You have no earlier notifications.")
                  : t("messages.empty", "You have no notifications yet.")}
            </div>
          ) : (
            <>
              {unreadNotifications.length > 0 && (
                <>
                  <div className="notification-group-title">
                    {t("groups.unread")}
                  </div>

                  {unreadNotifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      selectable={selectionMode}
                      selected={selectedIds.includes(notification.id)}
                      onToggleSelect={() => handleToggle(notification.id)}
                      onMarkRead={onMarkRead}
                      onClose={closeWithAnimation}
                    />
                  ))}
                </>
              )}

              {readNotifications.length > 0 && (
                <>
                  <div className="notification-group-title">
                    {t("groups.earlier")}
                  </div>

                  {readNotifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      selectable={selectionMode}
                      selected={selectedIds.includes(notification.id)}
                      onToggleSelect={() => handleToggle(notification.id)}
                      onMarkRead={onMarkRead}
                      onClose={closeWithAnimation}
                    />
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {confirmDialog.open &&
        createPortal(
          <div
            className="notification-confirm-backdrop"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div
              className="notification-confirm"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="notification-confirm__icon">
                <AlertTriangle size={22} />
              </div>

              <div className="notification-confirm__content">
                <h3>{confirmDialog.title}</h3>
                <p>{confirmDialog.text}</p>
              </div>

              <div className="notification-confirm__actions">
                <button
                  type="button"
                  className="notification-confirm__cancel"
                  onClick={() =>
                    setConfirmDialog({
                      open: false,
                      title: "",
                      text: "",
                      action: null,
                    })
                  }
                >
                  {t("buttons.cancelDelete")}
                </button>

                <button
                  type="button"
                  className="notification-confirm__delete"
                  onClick={confirmDialog.action}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <Loader2 size={16} className="spin" />
                      {t("messages.deleting", "Deleting...")}
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      {t("buttons.delete")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>,
    document.body,
  );
}
